import React, { useEffect, useMemo, useState, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { ArrowLeft, ChevronLeft, ChevronRight, ChevronDown, Plus, X, Printer, MoveHorizontal, Download, Hash, Phone, User as UserIcon, GripVertical, Pill, PauseCircle, PlayCircle, SkipForward, Info, History } from "lucide-react";
import { getRouteConfig } from "@/components/RouteIcon";
import { getMedicationColor } from "@/lib/medicationColors";
import MedicationDateStrip from "@/components/MedicationDateStrip";
import statusJson from "@/lib/medicationStatuses.json";

const MED_STATUSES = statusJson.medicationStatuses;
const MED_STATUS_MAP = Object.fromEntries(MED_STATUSES.map((s) => [s.status, s]));

const EVENT_TYPES = ["Drug Administration", "Clinic Visit", "Lab Draw", "Radiology", "Custom"];

const eventClass = (t) => ({
  "Drug Administration": "evt-drug",
  "Clinic Visit": "evt-clinic",
  "Lab Draw": "evt-lab",
  "Radiology": "evt-rad",
  "Custom": "evt-custom",
}[t] || "evt-custom");

const DEFAULT_COLOR_BY_TYPE = {
  "Clinic Visit": "#10B981",
  "Lab Draw": "#F59E0B",
  "Radiology": "#A855F7",
  "Custom": "#10B981",
};

const ROUTE_OPTIONS = ["Intravenous", "Oral", "Subcutaneous", "Intramuscular", "Topical"];
const SITE_OPTIONS = ["Facility Administered", "Home Administration", "Inpatient Admission", "Outpatient Clinic", "Pharmacy Pickup"];
const WEEKDAYS = [
  { id: 0, key: "Sun", letter: "S" },
  { id: 1, key: "Mon", letter: "M" },
  { id: 2, key: "Tue", letter: "T" },
  { id: 3, key: "Wed", letter: "W" },
  { id: 4, key: "Thu", letter: "T" },
  { id: 5, key: "Fri", letter: "F" },
  { id: 6, key: "Sat", letter: "S" },
];
const WEEKDAY_LETTER_TO_INDEX = { s: 0, m: 1, t: 2, w: 3, h: 4, f: 5, a: 6 }; // approximate parse for mwf etc.
const WEEKDAY_NAME_TO_INDEX = { sun: 0, mon: 1, tue: 2, wed: 3, thu: 4, fri: 5, sat: 6 };

function startOfMonth(d) { return new Date(d.getFullYear(), d.getMonth(), 1); }
function fmt(d) {
  const y = d.getFullYear(); const m = String(d.getMonth() + 1).padStart(2, "0"); const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}
function shortDate(iso) {
  if (!iso) return "";
  const d = new Date(iso + "T00:00:00");
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}
function parseISODate(iso) { return new Date(iso + "T00:00:00"); }
function daysBetween(startIso, iso) {
  const a = parseISODate(startIso); const b = parseISODate(iso);
  return Math.round((b - a) / 86400000);
}

/**
 * Day-pattern parser used by the new Add Event modal.
 * Returns an array of {date: ISO, dayNumber: number} for the given pattern + starting date.
 * Supports:
 *   - weekday-letter abbreviations like "mwf", "tth", "MWF"
 *   - weekday-name lists like "Mon Wed Fri"
 *   - cycle-day numerics like "1-5" or "1,3,5" or "1,4,8,11"
 * The "weekly" inputs are projected for ~12 weeks forward; cycle-day inputs are anchored to the start date.
 */
function parseDayPattern({ pattern, weekdays, startDate }) {
  const out = [];
  if (!startDate) return out;
  const start = parseISODate(startDate);

  // Path A — checkbox weekdays
  if (weekdays && weekdays.length > 0) {
    const wkSet = new Set(weekdays);
    for (let w = 0; w < 12; w++) {
      for (let d = 0; d < 7; d++) {
        const offset = w * 7 + d;
        const dt = new Date(start.getTime() + offset * 86400000);
        if (offset === 0) { // include start date if it matches
          if (wkSet.has(dt.getDay())) out.push({ date: fmt(dt), dayNumber: offset + 1 });
          continue;
        }
        if (wkSet.has(dt.getDay())) out.push({ date: fmt(dt), dayNumber: offset + 1 });
      }
    }
    return out;
  }

  if (!pattern || !pattern.trim()) {
    return [{ date: fmt(start), dayNumber: 1 }];
  }

  const raw = pattern.trim();

  // Path B1 — weekday names "Mon Wed Fri"
  const tokensSpaceComma = raw.toLowerCase().split(/[\s,]+/).filter(Boolean);
  const namedWeekdays = tokensSpaceComma
    .map((t) => WEEKDAY_NAME_TO_INDEX[t.slice(0, 3)])
    .filter((x) => x !== undefined);
  if (namedWeekdays.length === tokensSpaceComma.length && namedWeekdays.length > 0) {
    return parseDayPattern({ weekdays: namedWeekdays, startDate });
  }

  // Path B2 — "mwf", "tth", etc. — all letters?
  if (/^[smtwhfa]+$/i.test(raw)) {
    const letters = raw.toLowerCase().split("");
    const idxs = letters.map((l) => WEEKDAY_LETTER_TO_INDEX[l]).filter((x) => x !== undefined);
    if (idxs.length > 0) return parseDayPattern({ weekdays: idxs, startDate });
  }

  // Path C — numeric cycle days: "1-5, 7, 9-11"
  const cycleDays = new Set();
  raw.split(/[,;]/).forEach((segRaw) => {
    const seg = segRaw.trim();
    if (!seg) return;
    if (/^\d+\s*-\s*\d+$/.test(seg)) {
      const [a, b] = seg.split("-").map((x) => parseInt(x.trim(), 10));
      if (!isNaN(a) && !isNaN(b)) {
        for (let i = Math.min(a, b); i <= Math.max(a, b); i++) cycleDays.add(i);
      }
    } else if (/^\d+$/.test(seg)) {
      cycleDays.add(parseInt(seg, 10));
    }
  });
  if (cycleDays.size === 0) {
    return [{ date: fmt(start), dayNumber: 1 }];
  }
  return [...cycleDays].sort((a, b) => a - b).map((d) => {
    const dt = new Date(start.getTime() + (d - 1) * 86400000);
    return { date: fmt(dt), dayNumber: d };
  });
}

/**
 * Group events by drug treatment for the right sidebar.
 * Returns: [{ key, name, dose, route, site, color, days: [{day, date}] }]
 */
function groupDrugTreatments(events, startIso) {
  // Filter out skipped events — they were intentionally removed from the
  // active treatment plan and should NOT appear in the Treatment Schedule
  // Details panel (keeps desktop & mobile consistent).
  const drug = events.filter((e) => e.event_type === "Drug Administration" && e.status !== "skipped");
  const groups = new Map();
  drug.forEach((e) => {
    const key = `${e.drug_name || e.name}__${e.dose || ""}__${e.route || ""}__${e.site || e.location || ""}`;
    if (!groups.has(key)) {
      groups.set(key, {
        key,
        name: e.print_name || e.drug_name || e.name,
        dose: e.dose,
        route: e.route,
        site: e.site || e.location,
        color: e.color,
        days: [],
      });
    }
    const sorted = [...new Date(e.date).toISOString().slice(0, 10)];
    groups.get(key).days.push({ date: e.date, day: startIso ? daysBetween(startIso, e.date) + 1 : null });
  });
  // sort days within each group
  for (const g of groups.values()) g.days.sort((a, b) => a.date.localeCompare(b.date));
  return [...groups.values()];
}

export default function CalendarViewPage() {
  const { id } = useParams();
  const [cal, setCal] = useState(null);
  const [month, setMonth] = useState(new Date());
  const [showAdd, setShowAdd] = useState(false);
  const [showShift, setShowShift] = useState(false);
  const [showSkip, setShowSkip] = useState(false);
  const [selectedEv, setSelectedEv] = useState(null);
  const [icsBusy, setIcsBusy] = useState(false);
  const [dragOverDate, setDragOverDate] = useState(null);
  const draggingEventRef = useRef(null);

  const initialLoadDoneRef = useRef(false);
  const load = () => api.get(`/calendars/${id}`).then((r) => {
    setCal(r.data);
    // On first load only: anchor the calendar view at the CURRENT MONTH (not
    // the regimen's start date) so users land on Today regardless of when the
    // treatment plan started. After that, the user-selected month sticks.
    if (!initialLoadDoneRef.current) {
      initialLoadDoneRef.current = true;
      setMonth(new Date());
    }
  });

  useEffect(() => { load(); }, [id]);

  const eventsByDate = useMemo(() => {
    const map = {};
    (cal?.events || []).forEach((e) => { (map[e.date] = map[e.date] || []).push(e); });
    return map;
  }, [cal]);

  const days = useMemo(() => {
    const first = startOfMonth(month);
    const startWeekday = first.getDay();
    const gridStart = new Date(first);
    gridStart.setDate(first.getDate() - startWeekday);
    const arr = [];
    for (let i = 0; i < 42; i++) {
      const d = new Date(gridStart);
      d.setDate(gridStart.getDate() + i);
      arr.push(d);
    }
    return arr;
  }, [month]);

  const drugGroups = useMemo(() => groupDrugTreatments(cal?.events || [], cal?.start_date), [cal]);

  const onChipDragStart = (e, ev) => {
    draggingEventRef.current = ev;
    e.dataTransfer.effectAllowed = "move";
    try { e.dataTransfer.setData("text/plain", ev.id); } catch (err) { /* noop */ }
  };
  const onCellDragOver = (e, dateStr) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (dragOverDate !== dateStr) setDragOverDate(dateStr);
  };
  const onCellDrop = async (e, dateStr) => {
    e.preventDefault();
    setDragOverDate(null);
    const ev = draggingEventRef.current;
    draggingEventRef.current = null;
    if (!ev || ev.date === dateStr) return;
    // optimistic
    setCal((c) => c ? { ...c, events: c.events.map((x) => x.id === ev.id ? { ...x, date: dateStr } : x) } : c);
    try {
      await api.patch(`/calendars/${id}/events/${ev.id}`, { date: dateStr });
    } catch (err) {
      window.alert("Could not move the event. Refreshing.");
      load();
    }
  };

  const deleteEv = async (evId) => {
    if (!window.confirm("Delete this event?")) return;
    await api.delete(`/calendars/${id}/events/${evId}`);
    setSelectedEv(null);
    load();
  };

  const cyclesInCalendar = useMemo(() => {
    const seen = new Map();
    (cal?.events || []).forEach((e) => {
      if (e.cycle_index == null) return;
      if (!seen.has(e.cycle_index)) {
        seen.set(e.cycle_index, { index: e.cycle_index, label: e.cycle_label || `Cycle ${e.cycle_index + 1}` });
      }
    });
    return [...seen.values()].sort((a, b) => a.index - b.index);
  }, [cal]);

  const togglePause = async () => {
    if (!cal) return;
    try {
      if (cal.paused) {
        const resumeDate = window.prompt("Resume date (YYYY-MM-DD). Future events will be shifted by the pause duration.", fmt(new Date()));
        if (!resumeDate) return;
        await api.post(`/calendars/${id}/resume`, { resume_date: resumeDate, shift_future: true });
      } else {
        if (!window.confirm("Pause this regimen? Future events stay where they are until you resume.")) return;
        await api.post(`/calendars/${id}/pause`, { pause_date: fmt(new Date()) });
      }
      load();
    } catch (e) {
      window.alert(e?.response?.data?.detail || "Could not toggle pause state.");
    }
  };

  const skipCycle = async (cycleIndex, note) => {
    try {
      const r = await api.post(`/calendars/${id}/skip-cycle`, { cycle_index: cycleIndex, note: note || null });
      window.alert(`Skipped ${r.data.skipped} event(s) in this cycle.`);
      setShowSkip(false);
      load();
    } catch (e) {
      window.alert(e?.response?.data?.detail || "Could not skip cycle.");
    }
  };

  const exportIcs = async () => {
    setIcsBusy(true);
    try {
      const r = await api.get(`/calendars/${id}/export.ics`, { responseType: "blob" });
      const cd = r.headers["content-disposition"] || "";
      const m = /filename="?([^";]+)"?/i.exec(cd);
      const fname = m ? m[1] : `oncocadence-calendar-${id}.ics`;
      const blob = new Blob([r.data], { type: "text/calendar" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = fname;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      window.alert("Sorry, the calendar export failed. Please try again.");
    } finally {
      setIcsBusy(false);
    }
  };

  if (!cal) return <div className="p-8 text-stone-500">Loading calendar…</div>;

  const todayStr = fmt(new Date());
  const p = cal.patient || {};

  return (
    <div className="p-8 max-w-[1600px]" data-testid="calendar-view-page">
      <Link to={`/patients/${cal.patient_id}`} className="inline-flex items-center gap-2 text-stone-500 hover:text-stone-900 text-sm mb-4" data-testid="back-to-patient">
        <ArrowLeft className="w-4 h-4" />Back to patient
      </Link>

      {/* Header with patient summary + actions */}
      <div className="bg-white border border-stone-200 rounded-xl shadow-soft p-5 mb-5" data-testid="calendar-header-card">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <h1 className="font-display text-3xl font-bold text-stone-900">{cal.regimen_name}</h1>
            <div className="text-stone-500 mt-1 text-sm flex gap-2 flex-wrap">
              <span>Started {cal.start_date}</span><span>•</span>
              <span>{cal.events.length} events</span><span>•</span>
              <span>Provider: {cal.provider_name}</span>
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-stone-700" data-testid="calendar-patient-summary">
              <span className="inline-flex items-center gap-1.5 font-semibold text-stone-900" data-testid="cal-patient-name">
                <UserIcon className="w-4 h-4 text-stone-400" />
                {p.full_name || "Patient"}
              </span>
              {p.mrn && (
                <span className="inline-flex items-center gap-1.5" data-testid="cal-patient-mrn">
                  <Hash className="w-3.5 h-3.5 text-stone-400" />{p.mrn}
                </span>
              )}
              {p.phone && (
                <span className="inline-flex items-center gap-1.5" data-testid="cal-patient-phone">
                  <Phone className="w-3.5 h-3.5 text-stone-400" />{p.phone}
                </span>
              )}
            </div>
            {cal.paused && (
              <div className="mt-3 px-3 py-2 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-sm flex items-center gap-2" data-testid="paused-banner">
                <PauseCircle className="w-4 h-4 shrink-0" />
                <span>Regimen is paused since <b>{cal.pause_date}</b>{cal.pause_note ? ` — ${cal.pause_note}` : ""}. Click <b>Resume</b> to continue and shift remaining events.</span>
              </div>
            )}
          </div>
          <div className="flex gap-2 flex-wrap">
            <button onClick={togglePause} className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg border text-sm font-medium ${cal.paused ? "border-emerald-300 bg-emerald-50 text-emerald-700 hover:bg-emerald-100" : "border-stone-300 hover:bg-stone-50"}`} data-testid="pause-resume-button">
              {cal.paused ? (<><PlayCircle className="w-4 h-4" />Resume</>) : (<><PauseCircle className="w-4 h-4" />Pause</>)}
            </button>
            <button onClick={() => setShowSkip(true)} disabled={cyclesInCalendar.length === 0} className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-stone-300 hover:bg-stone-50 text-sm font-medium disabled:opacity-50" data-testid="skip-cycle-button">
              <SkipForward className="w-4 h-4" />Skip cycle
            </button>
            <button onClick={() => setShowShift(true)} className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-stone-300 hover:bg-stone-50 text-sm font-medium" data-testid="bulk-shift-button">
              <MoveHorizontal className="w-4 h-4" />Bulk shift
            </button>
            <button onClick={exportIcs} disabled={icsBusy} className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-stone-300 hover:bg-stone-50 text-sm font-medium disabled:opacity-60" data-testid="export-ics-button">
              <Download className="w-4 h-4" />{icsBusy ? "Exporting…" : "Export .ics"}
            </button>
            <button onClick={() => window.print()} className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-stone-300 hover:bg-stone-50 text-sm font-medium" data-testid="print-calendar-button">
              <Printer className="w-4 h-4" />Print
            </button>
            <button onClick={() => setShowAdd(true)} className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-brand-500 hover:bg-brand-700 text-white text-sm font-semibold" data-testid="add-event-button">
              <Plus className="w-4 h-4" />Add event
            </button>
          </div>
        </div>
      </div>

      {/* Medication Date strip — between header card and calendar grid.
          Lists every date with a Drug Administration event and shows a Switch
          per date for overall completion. Status flows to ChemoAnalytics. */}
      <MedicationDateStrip
        calId={id}
        events={cal?.events || []}
        onChanged={load}
        onSelectEvent={(ev) => setSelectedEv(ev)}
      />

      {/* 2-col layout: calendar + sidebar */}
      <div className="grid grid-cols-1 xl:grid-cols-[1fr_360px] gap-5">
        <div className="bg-white border border-stone-200 rounded-xl shadow-soft overflow-hidden">
          <div className="flex items-center justify-between px-5 py-4 border-b border-stone-100">
            <div className="flex items-center gap-2">
              <button onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() - 1, 1))} className="p-2 rounded-lg hover:bg-stone-100" data-testid="prev-month"><ChevronLeft className="w-4 h-4" /></button>
              <div className="font-display font-semibold text-lg text-stone-900 min-w-[180px] text-center">
                {month.toLocaleDateString(undefined, { month: "long", year: "numeric" })}
              </div>
              <button onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() + 1, 1))} className="p-2 rounded-lg hover:bg-stone-100" data-testid="next-month"><ChevronRight className="w-4 h-4" /></button>
            </div>
            <button onClick={() => setMonth(new Date())} className="text-sm px-3 py-1.5 rounded-lg border border-stone-300 hover:bg-stone-50">Today</button>
          </div>
          <div className="cal-grid bg-stone-50 text-xs uppercase tracking-wide text-stone-500 font-medium border-b border-stone-200">
            {["Sun","Mon","Tue","Wed","Thu","Fri","Sat"].map((d) => <div key={d} className="px-3 py-2 text-center">{d}</div>)}
          </div>
          <div className="cal-grid">
            {days.map((d, i) => {
              const ds = fmt(d);
              const inMonth = d.getMonth() === month.getMonth();
              const evs = eventsByDate[ds] || [];
              const isDragTarget = dragOverDate === ds;
              return (
                <div
                  key={i}
                  className={`cal-cell ${!inMonth ? "outside" : ""} ${ds === todayStr ? "today" : ""} ${isDragTarget ? "drag-over" : ""}`}
                  data-testid={`cell-${ds}`}
                  onDragOver={(e) => onCellDragOver(e, ds)}
                  onDragLeave={() => setDragOverDate(null)}
                  onDrop={(e) => onCellDrop(e, ds)}
                >
                  <div className="cal-cell-number">{d.getDate()}</div>
                  {evs.slice(0, 4).map((e) => {
                    const RouteI = e.event_type === "Drug Administration" && e.route ? getRouteConfig(e.route).Icon : null;
                    const isSkipped = e.status === "skipped";
                    const isDrug = e.event_type === "Drug Administration";
                    const medColor = isDrug ? getMedicationColor(e.drug_name || e.print_name || e.name) : null;
                    const chipStyle = medColor ? { backgroundColor: medColor.bg, color: medColor.text, borderColor: medColor.bg } : undefined;
                    const medStatus = e.med_status ? MED_STATUS_MAP[e.med_status] : null;
                    return (
                      <button
                        key={e.id}
                        className={`event-chip ${eventClass(e.event_type)} group/chip ${isSkipped ? "evt-skipped" : ""} ${isDrug ? "evt-medication-coloured" : ""}`}
                        style={chipStyle}
                        onClick={() => setSelectedEv(e)}
                        title={isSkipped ? `${e.print_name || e.name} · SKIPPED` : (e.route ? `${e.print_name || e.name} · ${e.route}` : (e.print_name || e.name))}
                        draggable={!isSkipped}
                        onDragStart={(ev) => onChipDragStart(ev, e)}
                        data-testid={`event-chip-${e.id}`}
                      >
                        <GripVertical className="w-2.5 h-2.5 inline-block mr-0.5 opacity-60 group-hover/chip:opacity-100 shrink-0" />
                        {RouteI && <RouteI className="w-3 h-3 inline-block mr-1 shrink-0" />}
                        {e.time ? `${e.time} ` : ""}{e.print_name || e.name}
                        {medStatus && <span className="ml-1 text-[10px] opacity-90" aria-label={`status ${medStatus.status}`}>{medStatus.emoji}</span>}
                        {e.confirmation === "confirmed" && <span className="ml-1 text-[10px]" title="Patient confirmed">✔️</span>}
                        {e.confirmation === "tentative" && <span className="ml-1 text-[10px]" title="Patient tentative">⏳</span>}
                        {e.confirmation === "declined" && <span className="ml-1 text-[10px]" title="Patient declined">⚠️</span>}
                      </button>
                    );
                  })}
                  {evs.length > 4 && <div className="text-[10px] text-stone-500 mt-0.5">+{evs.length - 4} more</div>}
                </div>
              );
            })}
          </div>
        </div>

        {/* Right Sidebar — Treatment Schedule + Status Reference */}
        <aside className="space-y-5 h-fit" data-testid="treatment-sidebar">
          <div className="bg-white border border-stone-200 rounded-xl shadow-soft p-5">
            <h3 className="font-display font-semibold text-lg text-stone-900">Treatment Schedule Details</h3>
            <p className="text-xs text-stone-500 mt-1">Each medication has its own colour across the whole calendar.</p>

            {drugGroups.length === 0 ? (
              <div className="mt-4 text-sm text-stone-500 italic">No drug administration events yet.</div>
            ) : (
              <div className="mt-4 space-y-3" data-testid="drug-treatment-list">
                {drugGroups.map((g) => {
                  const c = getMedicationColor(g.name);
                  return (
                    <div key={g.key} className="drug-treatment-tile relative pl-3 py-2 rounded-lg border" data-testid={`drug-treatment-${g.key}`}>
                      <span className="absolute left-0 top-2 bottom-2 w-1 rounded-full" style={{ background: c.bg }} />
                      <div className="flex items-start gap-1.5 text-sm">
                        <Pill className="w-4 h-4 shrink-0 mt-0.5" style={{ color: c.bg }} />
                        <div className="min-w-0">
                          <div className="font-semibold text-stone-900 leading-snug">
                            <span className="px-1.5 py-0.5 rounded text-[11px] font-bold mr-1.5 align-middle" style={{ backgroundColor: c.bg, color: c.text }}>{g.name}</span>
                            {g.dose && <span className="font-normal text-stone-600"> {g.dose}</span>}
                            {g.route && <span className="font-normal text-stone-500"> · {g.route}</span>}
                            {g.site && <span className="font-normal text-stone-500"> · {g.site}</span>}
                          </div>
                          <div className="text-xs mt-1 cycle-days-row">
                            <span className="cycle-days-label">Days: </span>
                            {g.days.map((d) => d.day).filter((x) => x != null).join(", ") || "—"}
                            {g.days.length > 0 && (
                              <>
                                {" "}<span className="cycle-days-paren">(</span>
                                <span className="cycle-days-dates">{g.days.map((d) => shortDate(d.date)).join(", ")}</span>
                                <span className="cycle-days-paren">)</span>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Compact Status Reference (collapsible) */}
          <StatusReferenceCard />
        </aside>
      </div>

      {/* Event detail popover */}
      {selectedEv && (
        <EventDetailModal
          ev={selectedEv}
          calId={id}
          onClose={() => setSelectedEv(null)}
          onDelete={deleteEv}
          onChanged={() => { load(); }}
        />
      )}

      {showSkip && (
        <SkipCycleModal
          cycles={cyclesInCalendar}
          onClose={() => setShowSkip(false)}
          onSubmit={skipCycle}
        />
      )}

      {showAdd && (
        <AddEventModal
          calId={id}
          startDateDefault={todayStr}
          onClose={() => setShowAdd(false)}
          onSaved={() => { setShowAdd(false); load(); }}
        />
      )}

      {showShift && (
        <BulkShiftModal
          calId={id}
          onClose={() => setShowShift(false)}
          onApplied={() => { setShowShift(false); load(); }}
        />
      )}
    </div>
  );
}


/* ----------------------------- SkipCycleModal ----------------------------- */
function SkipCycleModal({ cycles, onClose, onSubmit }) {
  const [cycleIndex, setCycleIndex] = useState(cycles?.[0]?.index ?? 0);
  const [note, setNote] = useState("");
  return (
    <div className="fixed inset-0 z-50 bg-stone-900/40 backdrop-blur-sm flex items-center justify-center p-4" data-testid="skip-cycle-modal">
      <div className="bg-white rounded-2xl shadow-elevated w-full max-w-md border border-stone-200 overflow-hidden">
        <div className="px-5 py-4 border-b border-stone-100 flex justify-between items-center">
          <h3 className="font-display font-semibold text-stone-900">Skip a cycle</h3>
          <button onClick={onClose}><X className="w-5 h-5 text-stone-400" /></button>
        </div>
        <div className="p-5 space-y-4">
          <p className="text-sm text-stone-600">Every event in the selected cycle will be marked as <b>skipped</b>. Already-completed events are left alone.</p>
          <div>
            <label className="text-sm font-medium text-stone-700 mb-1.5 block">Cycle</label>
            <select value={cycleIndex} onChange={(e) => setCycleIndex(parseInt(e.target.value, 10))}
              className="w-full px-3 py-2 rounded-lg border border-stone-300 focus:border-brand-500 focus:ring-2 focus:ring-brand-100 focus:outline-none"
              data-testid="skip-cycle-select">
              {cycles.map((c) => <option key={c.index} value={c.index}>{c.label}</option>)}
            </select>
          </div>
          <div>
            <label className="text-sm font-medium text-stone-700 mb-1.5 block">Reason (optional)</label>
            <textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)}
              placeholder="e.g. Patient hospitalized for neutropenia"
              className="w-full px-3 py-2 rounded-lg border border-stone-300 focus:border-brand-500 focus:ring-2 focus:ring-brand-100 focus:outline-none"
              data-testid="skip-cycle-note" />
          </div>
        </div>
        <div className="px-5 py-3 border-t border-stone-100 flex justify-end gap-2 bg-stone-50/40">
          <button onClick={onClose} className="px-3 py-1.5 rounded-lg border border-stone-300 text-sm font-medium hover:bg-stone-50">Cancel</button>
          <button onClick={() => onSubmit(cycleIndex, note)} className="px-3 py-1.5 rounded-lg bg-amber-600 hover:bg-amber-700 text-white text-sm font-semibold" data-testid="skip-cycle-confirm">Skip cycle</button>
        </div>
      </div>
    </div>
  );
}


/* ----------------------------- BulkShiftModal ----------------------------- */
function BulkShiftModal({ calId, onClose, onApplied }) {
  const [fromDate, setFromDate] = useState(fmt(new Date()));
  const [toDate, setToDate] = useState("");
  const [direction, setDirection] = useState("forward"); // forward | backward
  const [days, setDays] = useState(1);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  const signedDays = (direction === "forward" ? 1 : -1) * Math.max(0, parseInt(days || 0, 10));

  const fetchPreview = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/calendars/${calId}/bulk-shift/preview`, {
        days: signedDays,
        from_date: fromDate || null,
        to_date: toDate || null,
      });
      setPreview(r.data);
    } catch (e) {
      window.alert("Preview failed. Please check your inputs.");
    } finally {
      setBusy(false);
    }
  };

  const apply = async () => {
    if (!preview) {
      await fetchPreview();
    }
    setBusy(true);
    try {
      await api.post(`/calendars/${calId}/bulk-shift`, {
        days: signedDays,
        from_date: fromDate || null,
        to_date: toDate || null,
      });
      onApplied();
    } catch (e) {
      window.alert("Could not apply changes. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-stone-900/40 backdrop-blur-sm flex items-center justify-center p-4" data-testid="bulk-shift-modal">
      <div className="bg-white rounded-2xl shadow-elevated w-full max-w-lg border border-stone-200 overflow-hidden">
        <div className="px-5 py-4 border-b border-stone-100 flex justify-between items-center">
          <h3 className="font-display font-semibold text-stone-900">Bulk Date Adjustment</h3>
          <button onClick={onClose}><X className="w-5 h-5 text-stone-400" /></button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <div className="text-sm font-semibold text-stone-700 mb-2">Select Date Range</div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-stone-500 mb-1 block">From Date</label>
                <input type="date" value={fromDate} onChange={(e) => { setFromDate(e.target.value); setPreview(null); }}
                  className="w-full px-3 py-2 rounded-lg border border-stone-300 focus:border-brand-500 focus:ring-2 focus:ring-brand-100 focus:outline-none"
                  data-testid="shift-from-date" />
              </div>
              <div>
                <label className="text-xs text-stone-500 mb-1 block">To Date (optional)</label>
                <input type="date" value={toDate} onChange={(e) => { setToDate(e.target.value); setPreview(null); }}
                  className="w-full px-3 py-2 rounded-lg border border-stone-300 focus:border-brand-500 focus:ring-2 focus:ring-brand-100 focus:outline-none"
                  data-testid="shift-to-date" />
              </div>
            </div>
          </div>
          <div>
            <div className="text-sm font-semibold text-stone-700 mb-2">Adjustment</div>
            <div className="flex items-center gap-3">
              <select value={direction} onChange={(e) => { setDirection(e.target.value); setPreview(null); }}
                className="px-3 py-2 rounded-lg border border-stone-300 focus:border-brand-500 focus:ring-2 focus:ring-brand-100 focus:outline-none"
                data-testid="shift-direction">
                <option value="forward">Move Forward (+)</option>
                <option value="backward">Move Backward (−)</option>
              </select>
              <input type="number" min="0" value={days} onChange={(e) => { setDays(e.target.value); setPreview(null); }}
                className="w-24 px-3 py-2 rounded-lg border border-stone-300 focus:border-brand-500 focus:ring-2 focus:ring-brand-100 focus:outline-none"
                data-testid="shift-days-input" />
              <span className="text-sm text-stone-500">days</span>
            </div>
          </div>

          {preview && (
            <div className="rounded-lg border border-stone-200 bg-stone-50/70 p-3" data-testid="shift-preview-panel">
              <div className="text-sm font-semibold text-stone-800">
                Preview — {preview.shifted} event{preview.shifted === 1 ? "" : "s"} will move {signedDays >= 0 ? "+" : ""}{signedDays} day{Math.abs(signedDays) === 1 ? "" : "s"}
              </div>
              {preview.affected?.length > 0 ? (
                <ul className="mt-2 space-y-1 max-h-44 overflow-y-auto text-xs text-stone-700">
                  {preview.affected.slice(0, 50).map((a) => (
                    <li key={a.id} className="flex items-center justify-between gap-2 py-0.5">
                      <span className="truncate">{a.name} <span className="text-stone-400">({a.event_type})</span></span>
                      <span className="text-stone-500 whitespace-nowrap">{a.from_date} → <span className="text-stone-900 font-medium">{a.to_date}</span></span>
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="text-xs text-stone-500 mt-2 italic">No events match this range.</div>
              )}
            </div>
          )}
        </div>
        <div className="px-5 py-3 border-t border-stone-100 flex justify-end gap-2 bg-stone-50/40">
          <button onClick={onClose} className="px-3 py-1.5 rounded-lg border border-stone-300 text-sm font-medium hover:bg-stone-50" data-testid="shift-cancel-btn">Cancel</button>
          <button onClick={fetchPreview} disabled={busy} className="px-3 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold disabled:opacity-60" data-testid="shift-preview-btn">Preview</button>
          <button onClick={apply} disabled={busy} className="px-3 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold disabled:opacity-60" data-testid="shift-apply-btn">Apply Changes</button>
        </div>
      </div>
    </div>
  );
}


/* ----------------------------- AddEventModal ----------------------------- */
function AddEventModal({ calId, startDateDefault, onClose, onSaved }) {
  const [eventType, setEventType] = useState("Drug Administration");

  // shared
  const [startDate, setStartDate] = useState(startDateDefault);
  const [description, setDescription] = useState("");
  const [time, setTime] = useState("");
  const [color, setColor] = useState(DEFAULT_COLOR_BY_TYPE["Clinic Visit"]);

  // drug-only
  const [drugQuery, setDrugQuery] = useState("");
  const [drugListOpen, setDrugListOpen] = useState(false);
  const [selectedDrug, setSelectedDrug] = useState(null);
  const [printName, setPrintName] = useState("");
  const [dose, setDose] = useState("");
  const [route, setRoute] = useState("Intravenous");
  const [site, setSite] = useState("Facility Administered");

  // custom-only
  const [customName, setCustomName] = useState("");

  // day pattern
  const [patternEnabled, setPatternEnabled] = useState(false);
  const [weekdays, setWeekdays] = useState([]); // indices 0..6
  const [pattern, setPattern] = useState("");

  const [busy, setBusy] = useState(false);

  const isDrug = eventType === "Drug Administration";
  const isCustom = eventType === "Custom";
  const isClinical = ["Clinic Visit", "Lab Draw", "Radiology"].includes(eventType);

  useEffect(() => {
    if (!isDrug) setColor(DEFAULT_COLOR_BY_TYPE[eventType] || "#10B981");
  }, [eventType, isDrug]);

  // drug load — fetch full library once when drug administration is the selected event type
  const [allDrugs, setAllDrugs] = useState([]);
  useEffect(() => {
    if (!isDrug || allDrugs.length > 0) return;
    api.get("/drugs").then((r) => setAllDrugs(r.data || [])).catch(() => {});
  }, [isDrug, allDrugs.length]);
  // filter list when the user types into the search field
  const filteredDrugs = useMemo(() => {
    const q = drugQuery.trim().toLowerCase();
    if (!q) return allDrugs.slice(0, 200);
    return allDrugs.filter((d) => {
      const hay = `${d.name || ""} ${(d.brand_names || []).join(" ")} ${d.generic_name || ""}`.toLowerCase();
      return hay.includes(q);
    }).slice(0, 200);
  }, [allDrugs, drugQuery]);

  // (legacy debounced drug search kept commented; replaced by the full-library combobox above)

  const pickDrug = (d) => {
    setSelectedDrug(d);
    setDrugQuery("");
    setDrugListOpen(false);
    if (!printName) setPrintName((d.print_name || d.name).slice(0, 20));
    if (d.default_route && !route) setRoute(d.default_route);
    if (d.default_location && !site) setSite(d.default_location);
    if (d.color) setColor(d.color);
  };

  const toggleWeekday = (idx) => {
    setWeekdays((w) => w.includes(idx) ? w.filter((x) => x !== idx) : [...w, idx]);
  };

  const computeDates = () => {
    if (!patternEnabled) return [{ date: startDate, dayNumber: 1 }];
    return parseDayPattern({
      pattern: weekdays.length > 0 ? "" : pattern,
      weekdays,
      startDate,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isCustom && !customName.trim()) { window.alert("Custom Event Name is required."); return; }
    if (!startDate) { window.alert("Starting Date is required."); return; }

    const dates = computeDates();
    if (dates.length === 0) { window.alert("Could not parse the day pattern. Please review."); return; }

    let baseName = "";
    let baseExtras = {};
    if (isDrug) {
      if (!selectedDrug) { window.alert("Please select a drug from the dropdown."); return; }
      baseName = selectedDrug.name;
      baseExtras = {
        drug_id: selectedDrug.id || null,
        drug_name: selectedDrug.name || null,
        print_name: (printName || baseName).slice(0, 20),
        dose: dose || null,
        route,
        site,
        location: site,
      };
    } else if (isCustom) {
      baseName = customName.trim();
      baseExtras = { time: time || null, color };
    } else {
      baseName = eventType; // Clinic Visit / Lab Draw / Radiology — name = type
      baseExtras = { time: time || null, color };
    }

    setBusy(true);
    try {
      for (const d of dates) {
        const payload = {
          event_type: eventType,
          name: baseName,
          date: d.date,
          description: description || null,
          ...baseExtras,
        };
        // strip nulls so optional fields aren't sent as null when not needed
        Object.keys(payload).forEach((k) => payload[k] === null && delete payload[k]);
        await api.post(`/calendars/${calId}/events`, payload);
      }
      onSaved();
    } catch (e) {
      window.alert("Failed to save event(s). Please review and try again.");
    } finally {
      setBusy(false);
    }
  };

  const previewCount = computeDates().length;

  return (
    <div className="fixed inset-0 z-50 bg-stone-900/40 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in" data-testid="add-event-modal">
      <div className="bg-white rounded-2xl shadow-elevated w-full max-w-xl border border-stone-200 overflow-hidden max-h-[92vh] flex flex-col">
        <div className="px-5 py-4 border-b border-stone-100 flex justify-between items-center">
          <h3 className="font-display font-semibold text-stone-900">Add New Event</h3>
          <button onClick={onClose}><X className="w-5 h-5 text-stone-400" /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-4 overflow-y-auto">
          {/* Event type */}
          <div>
            <label className="text-sm font-medium text-stone-700 mb-1.5 block">Event Type</label>
            <select data-testid="event-type-select" value={eventType} onChange={(e) => setEventType(e.target.value)}
              className="w-full px-3 py-2 rounded-lg border border-stone-300 focus:border-brand-500 focus:ring-2 focus:ring-brand-100 focus:outline-none">
              {EVENT_TYPES.map((t) => <option key={t}>{t}</option>)}
            </select>
          </div>

          {/* Drug Administration block */}
          {isDrug && (
            <>
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-sm font-medium text-stone-700">Select Drug <span className="text-stone-400 font-normal">— pick from the drug library</span></label>
                  <Link to="/drugs" className="text-xs px-2 py-1 rounded bg-violet-100 text-violet-700 font-semibold hover:bg-violet-200" data-testid="view-drug-database-link">View Drug Database</Link>
                </div>
                <div className="relative">
                  {/* Combobox trigger — looks like a <select> but supports type-ahead */}
                  <button type="button" onClick={() => setDrugListOpen((v) => !v)}
                    className="w-full pl-3 pr-9 py-2 rounded-lg border border-stone-300 hover:border-stone-400 focus:border-brand-500 focus:ring-2 focus:ring-brand-100 focus:outline-none text-left bg-white"
                    data-testid="drug-dropdown-trigger" aria-expanded={drugListOpen}>
                    <span className={selectedDrug ? "text-stone-900" : "text-stone-400"}>
                      {selectedDrug ? selectedDrug.name : "-- Choose a medication from the library --"}
                    </span>
                    <ChevronDown className={`absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400 transition-transform ${drugListOpen ? "rotate-180" : ""}`} />
                  </button>
                  {drugListOpen && (
                    <div className="absolute z-30 left-0 right-0 mt-1 bg-white border border-stone-200 rounded-lg shadow-elevated overflow-hidden" data-testid="drug-dropdown-panel">
                      <div className="p-2 border-b border-stone-100 bg-stone-50/60">
                        <input
                          autoFocus
                          value={drugQuery}
                          onChange={(e) => setDrugQuery(e.target.value)}
                          placeholder={`Search ${allDrugs.length || ""} drugs by name or brand…`}
                          className="w-full px-2.5 py-1.5 rounded-md border border-stone-300 text-sm focus:border-brand-500 focus:ring-2 focus:ring-brand-100 focus:outline-none"
                          data-testid="drug-search-input"
                        />
                      </div>
                      <div className="max-h-64 overflow-y-auto" data-testid="drug-search-results">
                        {filteredDrugs.length === 0 ? (
                          <div className="px-3 py-4 text-sm text-stone-500 text-center">No drugs match "{drugQuery}".</div>
                        ) : (
                          filteredDrugs.map((d) => (
                            <button type="button" key={d.id} onClick={() => pickDrug(d)} className="w-full text-left px-3 py-2 hover:bg-stone-50 text-sm border-b border-stone-50 last:border-0" data-testid={`drug-option-${d.id}`}>
                              <div className="font-medium text-stone-900">{d.name}</div>
                              {d.brand_names?.length > 0 && <div className="text-xs text-stone-500">{d.brand_names.join(", ")}</div>}
                            </button>
                          ))
                        )}
                      </div>
                      <div className="px-3 py-2 border-t border-stone-100 bg-stone-50/60 flex items-center justify-between text-xs text-stone-500">
                        <span>{filteredDrugs.length} of {allDrugs.length} drugs</span>
                        <button type="button" onClick={() => setDrugListOpen(false)} className="font-medium text-stone-600 hover:text-stone-900" data-testid="drug-dropdown-close">Close</button>
                      </div>
                    </div>
                  )}
                </div>
                <p className="text-xs text-stone-500 mt-1">Click the dropdown to browse the full library, then start typing to filter.</p>
              </div>

              <div>
                <label className="text-sm font-medium text-stone-700 mb-1.5 block">Print Name (max 20 chars)</label>
                <input value={printName} maxLength={20} onChange={(e) => setPrintName(e.target.value.slice(0, 20))}
                  placeholder="Auto-generated abbreviation"
                  className="w-full px-3 py-2 rounded-lg border border-stone-300 focus:border-brand-500 focus:ring-2 focus:ring-brand-100 focus:outline-none"
                  data-testid="print-name-input" />
                <p className="text-xs text-stone-500 mt-1">Abbreviated name for calendar display. Auto-populated when you select a drug.</p>
              </div>

              <div>
                <label className="text-sm font-medium text-stone-700 mb-1.5 block">Dose</label>
                <input value={dose} onChange={(e) => setDose(e.target.value)} placeholder="e.g., 375mg/m2, 100mg"
                  className="w-full px-3 py-2 rounded-lg border border-stone-300 focus:border-brand-500 focus:ring-2 focus:ring-brand-100 focus:outline-none"
                  data-testid="dose-input" />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-medium text-stone-700 mb-1.5 block">Route of Administration</label>
                  <select value={route} onChange={(e) => setRoute(e.target.value)}
                    className="w-full px-3 py-2 rounded-lg border border-stone-300 focus:border-brand-500 focus:ring-2 focus:ring-brand-100 focus:outline-none"
                    data-testid="route-select">
                    {ROUTE_OPTIONS.map((r) => <option key={r} value={r}>{r === "Intravenous" ? "Intravenous (IV)" : r}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-sm font-medium text-stone-700 mb-1.5 block">Site of Administration</label>
                  <select value={site} onChange={(e) => setSite(e.target.value)}
                    className="w-full px-3 py-2 rounded-lg border border-stone-300 focus:border-brand-500 focus:ring-2 focus:ring-brand-100 focus:outline-none"
                    data-testid="site-select">
                    {SITE_OPTIONS.map((s) => <option key={s}>{s}</option>)}
                  </select>
                </div>
              </div>
            </>
          )}

          {/* Clinic / Lab / Radiology block */}
          {isClinical && (
            <>
              <div>
                <label className="text-sm font-medium text-stone-700 mb-1.5 block">Time (optional)</label>
                <input type="time" value={time} onChange={(e) => setTime(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg border border-stone-300 focus:border-brand-500 focus:ring-2 focus:ring-brand-100 focus:outline-none"
                  data-testid="event-time-input" />
              </div>
              <div>
                <label className="text-sm font-medium text-stone-700 mb-1.5 block">Color</label>
                <input type="color" value={color} onChange={(e) => setColor(e.target.value)}
                  className="w-full h-10 rounded-lg border border-stone-300"
                  data-testid="event-color-input" />
              </div>
            </>
          )}

          {/* Custom block */}
          {isCustom && (
            <>
              <div>
                <label className="text-sm font-medium text-stone-700 mb-1.5 block">Custom Event Name <span className="text-red-500">*</span></label>
                <input value={customName} onChange={(e) => setCustomName(e.target.value)} required
                  placeholder="Enter custom event name"
                  className="w-full px-3 py-2 rounded-lg border border-stone-300 focus:border-brand-500 focus:ring-2 focus:ring-brand-100 focus:outline-none"
                  data-testid="custom-event-name" />
              </div>
              <div>
                <label className="text-sm font-medium text-stone-700 mb-1.5 block">Time (optional)</label>
                <input type="time" value={time} onChange={(e) => setTime(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg border border-stone-300 focus:border-brand-500 focus:ring-2 focus:ring-brand-100 focus:outline-none"
                  data-testid="event-time-input" />
              </div>
              <div>
                <label className="text-sm font-medium text-stone-700 mb-1.5 block">Color</label>
                <input type="color" value={color} onChange={(e) => setColor(e.target.value)}
                  className="w-full h-10 rounded-lg border border-stone-300"
                  data-testid="event-color-input" />
              </div>
            </>
          )}

          {/* Day pattern (optional) */}
          <div className="rounded-lg border border-stone-200 p-3 bg-stone-50/40">
            <label className="flex items-center gap-2 text-sm font-medium text-stone-700">
              <input type="checkbox" checked={patternEnabled} onChange={(e) => setPatternEnabled(e.target.checked)} data-testid="pattern-enable" />
              Day Pattern (optional)
            </label>
            {patternEnabled && (
              <div className="mt-3 space-y-3">
                <div>
                  <div className="text-xs text-stone-500 mb-1.5">Select days of week:</div>
                  <div className="flex flex-wrap gap-2" data-testid="pattern-weekday-row">
                    {WEEKDAYS.map((w) => (
                      <label key={w.id} className="flex flex-col items-center gap-1 px-2 py-1.5 rounded-lg border border-stone-200 bg-white text-xs">
                        <input type="checkbox" checked={weekdays.includes(w.id)} onChange={() => toggleWeekday(w.id)} data-testid={`pattern-weekday-${w.key.toLowerCase()}`} />
                        <span>{w.key}</span>
                      </label>
                    ))}
                  </div>
                </div>
                <div>
                  <input value={pattern} onChange={(e) => setPattern(e.target.value)} disabled={weekdays.length > 0}
                    placeholder="Or type pattern: mwf, 1-5, 1,3,5"
                    className="w-full px-3 py-2 rounded-lg border border-stone-300 focus:border-brand-500 focus:ring-2 focus:ring-brand-100 focus:outline-none disabled:bg-stone-100 disabled:text-stone-400"
                    data-testid="pattern-text-input" />
                  <p className="text-xs text-stone-500 mt-1">Check boxes above OR type pattern. Formats: mwf/MWF, Mon Wed Fri, 1-5, 1,3,5 (cycle days).</p>
                </div>
                <div className="text-xs font-medium text-emerald-700">
                  Will create <span data-testid="pattern-preview-count">{previewCount}</span> event{previewCount === 1 ? "" : "s"}.
                </div>
              </div>
            )}
          </div>

          <div>
            <label className="text-sm font-medium text-stone-700 mb-1.5 block">Starting Date</label>
            <input type="date" required value={startDate} onChange={(e) => setStartDate(e.target.value)}
              className="w-full px-3 py-2 rounded-lg border border-stone-300 focus:border-brand-500 focus:ring-2 focus:ring-brand-100 focus:outline-none"
              data-testid="event-start-date" />
          </div>

          <div>
            <label className="text-sm font-medium text-stone-700 mb-1.5 block">Description (optional)</label>
            <textarea rows={2} value={description} onChange={(e) => setDescription(e.target.value)}
              placeholder="Event description"
              className="w-full px-3 py-2 rounded-lg border border-stone-300 focus:border-brand-500 focus:ring-2 focus:ring-brand-100 focus:outline-none"
              data-testid="event-description-input" />
          </div>
        </form>
        <div className="px-5 py-3 border-t border-stone-100 flex justify-end gap-2 bg-stone-50/40">
          <button onClick={onClose} type="button" className="px-3 py-2 rounded-lg border border-stone-300 text-stone-700 text-sm font-medium hover:bg-stone-50">Cancel</button>
          <button onClick={handleSubmit} type="button" disabled={busy} className="px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold disabled:opacity-60" data-testid="event-save-btn">
            {busy ? "Saving…" : "Add Event(s)"}
          </button>
        </div>
      </div>
    </div>
  );
}


/* ----------------------------- EventDetailModal ----------------------------- */
function EventDetailModal({ ev, calId, onClose, onDelete, onChanged }) {
  const isDrug = ev.event_type === "Drug Administration";
  const medColor = isDrug ? getMedicationColor(ev.drug_name || ev.print_name || ev.name) : null;
  const currentStatus = ev.med_status || (ev.status === "skipped" ? "Skipped" : "Scheduled");
  const [status, setStatus] = useState(currentStatus);
  const [statusNote, setStatusNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [localHistory, setLocalHistory] = useState(ev.status_history || []);

  const saveStatus = async () => {
    if (status === currentStatus && !statusNote.trim()) return;
    setBusy(true);
    try {
      const r = await api.patch(`/calendars/${calId}/events/${ev.id}`, { med_status: status, status_note: statusNote || null });
      setLocalHistory(r.data.event.status_history || []);
      setStatusNote("");
      onChanged?.();
    } catch (e) {
      window.alert("Could not update medication status.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-stone-900/40 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in" data-testid="event-detail-modal" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-elevated w-full max-w-md border border-stone-200 overflow-hidden max-h-[92vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-stone-100 flex justify-between items-center" style={medColor ? { background: `linear-gradient(135deg, ${medColor.bg}18 0%, transparent 60%)` } : {}}>
          <div>
            <div className="text-xs uppercase tracking-wider text-stone-500">{ev.event_type}</div>
            <h3 className="font-display font-semibold text-lg text-stone-900 mt-1 flex items-center gap-2">
              {medColor && <span className="w-3 h-3 rounded-full" style={{ background: medColor.bg }} />}
              {ev.print_name || ev.name}
            </h3>
          </div>
          <button onClick={onClose}><X className="w-5 h-5 text-stone-400" /></button>
        </div>
        <div className="p-5 space-y-2 text-sm text-stone-700 overflow-y-auto">
          <div><span className="text-stone-500">Date:</span> <span className="text-stone-900 font-medium">{ev.date}</span>{ev.time ? ` at ${ev.time}` : ""}</div>
          {ev.drug_name && ev.drug_name !== ev.name && <div><span className="text-stone-500">Drug:</span> {ev.drug_name}</div>}
          {ev.dose && <div><span className="text-stone-500">Dose:</span> {ev.dose}</div>}
          {ev.route && <div><span className="text-stone-500">Route:</span> {ev.route}</div>}
          {ev.site && <div><span className="text-stone-500">Site:</span> {ev.site}</div>}
          {ev.location && !ev.site && <div><span className="text-stone-500">Location:</span> {ev.location}</div>}
          {ev.cycle_label && <div><span className="text-stone-500">Cycle:</span> {ev.cycle_label} (Day {ev.cycle_day})</div>}
          {ev.description && <div className="pt-2 border-t border-stone-100"><div className="text-stone-500">Notes</div><div>{ev.description}</div></div>}

          {isDrug && (
            <div className="pt-3 mt-1 border-t border-stone-100" data-testid="event-medication-status">
              <div className="text-[11px] uppercase tracking-wider text-stone-500 font-semibold mb-1.5 flex items-center gap-1"><Pill className="w-3.5 h-3.5" />Medication status</div>
              <select value={status} onChange={(e) => setStatus(e.target.value)} className="w-full px-3 py-2 rounded-lg border border-stone-300 bg-white text-sm focus:border-brand-500 focus:ring-2 focus:ring-brand-100 focus:outline-none mb-2" data-testid="event-status-select">
                {MED_STATUSES.map((s) => <option key={s.status} value={s.status}>{s.emoji} {s.status}</option>)}
              </select>
              <input value={statusNote} onChange={(e) => setStatusNote(e.target.value)} placeholder="Reason (optional, e.g. 'Recent skin rash flare')" className="w-full px-3 py-2 rounded-lg border border-stone-300 text-sm focus:border-brand-500 focus:ring-2 focus:ring-brand-100 focus:outline-none mb-2" data-testid="event-status-note" />
              <button onClick={saveStatus} disabled={busy || (status === currentStatus && !statusNote.trim())} className="w-full px-3 py-1.5 rounded-lg bg-brand-500 hover:bg-brand-700 text-white text-sm font-semibold disabled:opacity-60" data-testid="event-status-save-btn">
                {busy ? "Saving…" : "Update status"}
              </button>
              {localHistory.length > 0 && (
                <div className="mt-3" data-testid="event-status-history">
                  <div className="text-[11px] uppercase tracking-wider text-stone-500 font-semibold mb-1 flex items-center gap-1"><History className="w-3.5 h-3.5" />Status history</div>
                  <ul className="space-y-1 max-h-32 overflow-y-auto text-xs">
                    {localHistory.slice().reverse().slice(0, 8).map((h, i) => {
                      const s = MED_STATUS_MAP[h.status];
                      return (
                        <li key={i} className="flex items-start gap-1.5 py-1 border-t border-stone-50 first:border-0">
                          <span className="text-sm leading-none mt-0.5">{s?.emoji || "•"}</span>
                          <div className="min-w-0 flex-1">
                            <div><b style={{ color: s?.color }}>{h.status}</b> {h.note && <span className="text-stone-600">· {h.note}</span>}</div>
                            <div className="text-stone-400">{new Date(h.updated_at).toLocaleString()} {h.updated_by_name && `· ${h.updated_by_name}`}</div>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
        <div className="px-5 py-3 border-t border-stone-100 flex justify-end gap-2 bg-stone-50/40">
          <button onClick={() => onDelete(ev.id)} className="px-3 py-1.5 rounded-lg bg-red-50 text-red-700 hover:bg-red-100 text-sm font-medium border border-red-200" data-testid="delete-event-btn">Delete</button>
          <button onClick={onClose} className="px-3 py-1.5 rounded-lg bg-stone-100 hover:bg-stone-200 text-sm font-medium">Close</button>
        </div>
      </div>
    </div>
  );
}


/* ----------------------------- StatusReferenceCard ----------------------------- */
function StatusReferenceCard() {
  const [open, setOpen] = useState(false);
  return (
    <div className="bg-white border border-stone-200 rounded-xl shadow-soft p-5" data-testid="status-reference-card">
      <button onClick={() => setOpen((v) => !v)} className="w-full flex items-center justify-between text-left" data-testid="status-reference-toggle">
        <div className="flex items-center gap-2">
          <Info className="w-4 h-4 text-stone-400" />
          <h3 className="font-display font-semibold text-stone-900 text-base">{open ? "Hide status reference" : "Show status reference"}</h3>
        </div>
        <ChevronDown className={`w-4 h-4 text-stone-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="mt-3 space-y-1.5" data-testid="status-reference-list">
          {MED_STATUSES.map((s) => (
            <div key={s.status} className="flex items-start gap-2 text-xs leading-snug">
              <span className="text-base leading-none shrink-0">{s.emoji}</span>
              <div>
                <span className="font-semibold" style={{ color: s.color }}>{s.status}</span>
                <span className="text-stone-600"> — {s.description}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
